import { type JSX, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import BaseNavbar from '@/shared/components/BaseNavbar';
import { Layout } from '@/shared/components/Layout';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useScreenSize from '@/shared/hooks/useScreenSize';
import { SignIn } from '../components/Onboard/Steps/SignIn';
import { ViewOnlySignIn } from '../components/Onboard/Steps/ViewOnlySignIn';
import useOffboarding from '../hooks/useOffboarding';
import { useStateChainAccount } from '../hooks/useStateChainAccount';
import Hero from '../public/bg-circular-gradient.svg';
import { getSafeRedirectPath } from '../utils/getSafeRedirectPath';

export default function Index(): JSX.Element {
  const router = useRouter();
  const signedIn = useRef(false);
  const { selectedAccount, selectAccount } = usePolkadot();
  const { accountInfo } = useStateChainAccount();
  const offboard = useOffboarding();

  const { isMobile } = useScreenSize();

  useEffect(() => {
    if (accountInfo) {
      if (accountInfo.role === 'liquidity_provider') {
        router.push(
          getSafeRedirectPath(router.query.redirectUrl, window.location.origin, '/portfolio'),
        );
      } else if (accountInfo.role === 'unregistered' && selectedAccount?.type === 'evm') {
        // if this is a delegator, they have a FLIP balance > 0 and cannot use
        // account creation deposit channels to on board. we send them to the
        // onboarding flow to register instead
        if (accountInfo.flip_balance > 0n) {
          router.push('/onboard');
        } else {
          router.push('/balances');
        }
      } else if (offboard.pendingRedemption) {
        router.push('/offboard');
      } else if (!offboard.redemptionLoading && !offboard.pendingRedemption && signedIn.current) {
        router.push('/onboard');
      } else if (!offboard.redemptionLoading) {
        selectAccount(null);
      }
    }
  }, [accountInfo, offboard.redemptionLoading, offboard.pendingRedemption]);

  return (
    <Layout
      navbar={
        <BaseNavbar
          logoHref="/"
          className={classNames(isMobile && 'h-min', 'border-b border-b-cf-gray-3')}
        />
      }
    >
      <div
        className={classNames(
          isMobile ? 'w-full justify-start p-2' : 'justify-center',
          'flex grow flex-col items-center',
        )}
      >
        <Hero className="pointer-events-none absolute top-[-80px] hidden h-full md:block" />
        {/* eslint-disable-next-line no-nested-ternary */}
        {selectedAccount !== null ? (
          <LoadingSpinner />
        ) : isMobile ? (
          <ViewOnlySignIn />
        ) : (
          <SignIn
            onCompleted={() => {
              signedIn.current = true;
            }}
          />
        )}
      </div>
    </Layout>
  );
}
